import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { env } from './env';
import { generateCodeChallenge, generateCodeVerifier } from './pkce';
import { getAuthStorage } from './auth-storage';

const VERIFIER_KEY = 'quon.pkce.verifier';
const PROVIDER_KEY = 'quon.pkce.provider';

export type OAuthProvider = 'google' | 'github' | 'apple';

/** サインアップ / パスワードリセット共通の最小パスワード長。 */
export const MIN_PASSWORD_LENGTH = 8;

let client: SupabaseClient | null = null;

/**
 * Lazily-instantiated Supabase client.
 *
 * `flowType: 'pkce'` switches to the spec-compliant code flow.
 */
export function getSupabase(): SupabaseClient {
  if (!client) {
    client = createClient(env.supabaseUrl(), env.supabaseAnonKey(), {
      auth: {
        flowType: 'pkce',
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'quon.supabase.auth',
      },
    });
  }
  return client;
}

function redirectUri(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/auth/callback`;
}

function resetPasswordRedirectUri(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/auth/reset-password`;
}

/**
 * Starts the OAuth code flow.
 *
 * Returns the authorize URL so the caller can follow it. The PKCE
 * `code_verifier` is stashed in `AuthStorage` until `completeSignIn`
 * consumes it.
 */
export async function startSignIn(provider: OAuthProvider = 'google'): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  const storage = getAuthStorage();
  await storage.set(VERIFIER_KEY, verifier);
  await storage.set(PROVIDER_KEY, provider);

  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectUri(),
      skipBrowserRedirect: true,
      queryParams: {
        code_challenge: challenge,
        code_challenge_method: 'S256',
      },
    },
  });
  if (error || !data.url) {
    throw error ?? new Error('signInWithOAuth returned no url');
  }
  return data.url;
}

/**
 * Email + password sign-in. Returns the session immediately (no redirect).
 *
 * Apps that prefer magic-links can call `signInWithOtp` instead — exposed
 * by the Supabase client via `getSupabase().auth.signInWithOtp(...)`.
 */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

/**
 * 招待コード付きサインアップ (spec #44)。
 *
 * Supabase の signup は無効のままなので、`supabase.auth.signUp` は使わず、
 * apps/api の `POST /v1/auth/signup` に招待コードを渡してサーバ側(service role)で
 * ユーザーを作ってもらう。成功したらそのまま `signInWithPassword` でログインする。
 *
 * エラーは code に丸めて投げる（'invalid_invite' / 'registration_failed' /
 * 'rate_limited' / 'signup_failed'）。UI はコードで文言を出し分ける。
 */
export async function signUpWithInvite(
  email: string,
  password: string,
  inviteCode: string,
): Promise<void> {
  const cleanEmail = email.trim();
  let res: Response;
  try {
    res = await fetch(`${env.apiBaseUrl}/v1/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, password, inviteCode }),
    });
  } catch {
    throw new Error('network_error'); // オフライン等は「入力を確認」でなく通信エラー扱い。
  }
  if (!res.ok) {
    if (res.status === 429) throw new Error('rate_limited');
    let reason = 'signup_failed';
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) reason = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(reason);
  }
  // 作成成功 → そのままログイン。
  await signInWithPassword(cleanEmail, password);
}

/**
 * パスワードリセットのメールリンクを送る。
 *
 * Supabase はメールの存在有無に関わらず成功レスポンスを返す（列挙攻撃対策）ので、
 * ここも常に成功として扱い、UI 側は「メールを確認してください」の一律文言を出す
 * （`error` があっても投げない・ユーザーには見せない）。ただし SMTP 設定不備等の
 * インフラ障害は診断できないと困るので、コンソールにだけは残す（レビュー MEDIUM）。
 * 招待制はサインアップ (`signUpWithInvite`) 側の不変条件であり、既存ユーザーの
 * パスワード再設定はそれとは別（新規アカウントは作られない）。
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: resetPasswordRedirectUri(),
  });
  if (error) console.error('[auth] resetPasswordForEmail failed', error);
}

/**
 * パスワードリセットのメールリンク（`?code=...`）を踏んだ後、そのコードを
 * セッションに交換する。OAuth の `completeSignIn` と同じ PKCE code-exchange だが、
 * こちらは supabase-js が `resetPasswordForEmail` 呼び出し時に自前で verifier を
 * 管理する（OAuth 用の `AuthStorage` とは無関係）。
 */
export async function completeRecovery(callback: URL): Promise<void> {
  const code = callback.searchParams.get('code');
  if (!code) throw new Error('missing_code');

  const supabase = getSupabase();
  const { error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) throw error;
}

/** 新しいパスワードを設定する。呼び出し前に `completeRecovery` でセッションが必要。 */
export async function updatePassword(newPassword: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

/**
 * Exchanges an OAuth `code` for a Supabase session. Wired to the boot-time
 * callback watcher below and the `/auth/callback` route.
 */
export async function completeSignIn(callback: URL): Promise<void> {
  const code = callback.searchParams.get('code');
  if (!code) throw new Error('callback missing code');

  const supabase = getSupabase();
  const { error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) throw error;

  const storage = getAuthStorage();
  await storage.remove(VERIFIER_KEY);
  await storage.remove(PROVIDER_KEY);
}

/* ----- OAuth callback bootstrap --------------------------------------- */

/**
 * Watches the current URL for the OAuth `code` query param (the
 * `/auth/callback` route handles the actual exchange on a full page load;
 * this catches SPA history navigations). Formerly the web fallback of the
 * Tauri `quon://` deep-link subscription — the Tauri branch was removed with
 * the Tauri shell (判定ボード D-1, 2026-07-05), keeping the web behaviour
 * identical.
 *
 * Scoped to `/auth/callback` only (security-reviewer 指摘): without the path
 * check this fires on ANY route with a `?code=` param, including
 * `/auth/reset-password`, which exchanges its own recovery `code` via
 * `completeRecovery`. Both calling `exchangeCodeForSession` on the same
 * single-use PKCE code raced — whichever lost got a spurious "リンクが無効"
 * error while the other silently established a session.
 *
 * Returns an unsubscribe function.
 */
function onAuthCallbackUrl(handler: (url: URL) => void): () => void {
  const fire = () => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/auth/callback') return;
    const params = new URL(window.location.href);
    if (params.searchParams.has('code')) handler(params);
  };
  fire();
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', fire);
    return () => window.removeEventListener('popstate', fire);
  }
  return () => {};
}

let bootUnsub: (() => void) | null = null;

/**
 * Wires the OAuth callback watcher once at app boot. Idempotent; safe to
 * call from any provider.
 */
export async function bootAuth(): Promise<() => void> {
  if (bootUnsub) return bootUnsub;
  bootUnsub = onAuthCallbackUrl((url) => {
    completeSignIn(url).catch((err) => {
      console.error('[auth] PKCE exchange failed', err);
    });
  });
  return bootUnsub;
}

/* ----- Session helpers ----------------------------------------------- */

export async function getAccessToken(): Promise<string | null> {
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function getCurrentUser(): Promise<{ id: string; email: string | null } | null> {
  const supabase = getSupabase();
  const { data } = await supabase.auth.getUser();
  if (!data.user) return null;
  return { id: data.user.id, email: data.user.email ?? null };
}

export async function signOut(): Promise<void> {
  await getSupabase().auth.signOut();
}
